import { useContainerContext, useContainerLayout, useIsContainerInModal } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { useMemo } from 'react';
import { useVizContainerLayout } from '@/components/VizContainer/hooks/internal/useVizContainerLayout';
import { PanelLayoutStrategy } from '@/core';
import { VizPanelProps } from './model';
import { VizPanelHeaderProps } from './VizPanelHeader';
import { OVERRIDE_BAR_HEIGHT } from './VizPanelTimeOverrideBar';

export interface UseVizPanelLayoutProps
  extends Pick<Required<VizPanelProps>, 'layoutStrategy'>,
    Pick<VizPanelProps, 'width' | 'height'> {}

export interface UseVizPanelLayoutResponse {
  /**
   * Layout to apply to the `Container`.
   */
  container: {
    width?: number;
    height?: number;
    maxHeight?: number;
  };
}

const heightPropMapping: Record<PanelLayoutStrategy, string | undefined> = {
  // Set explicit height because it should exactly fit the content without growing or shrinking.
  fill: 'height',
  // No height values set because we want to let the height grow and shrink.
  'flex-height': undefined,
};

/**
 * Hook that provides some constants related to the layout of the `VizContainer.Root` in `VizPanel`.
 *
 * See the other hooks in this file for related hooks for managing the layout of `VizPanel` children. These hooks are
 * separated because of various context dependencies.
 */
export const useVizPanelLayout = ({
  width,
  height,
  layoutStrategy,
}: UseVizPanelLayoutProps): UseVizPanelLayoutResponse => {
  return useMemo(() => {
    // Use `height` for a consistent "fill" of the height. Otherwise, use `maxHeight` for a more
    // dynamic height.
    const heightProp = heightPropMapping[layoutStrategy];

    return {
      container: {
        // The width is static. Only the height is dynamic because the `Container` is a flex layout
        // with a column direction. Dynamic widths are usually more about the page layout (not the
        // size of the content), so can be more easily handled by the layout around the `VizPanel`.
        width,

        // Doing this ternary because the height prop sometimes is `undefined`.
        ...(heightProp ? { [heightProp]: height } : {}),
      },
    };
  }, [height, layoutStrategy, width]);
};

export interface UseVizPanelChildrenLayoutProps
  extends Pick<VizPanelProps, 'title' | 'enableTimeOverrideBar'>,
    Pick<VizPanelHeaderProps, 'showRelativeTimeChip'> {
  hasErrors?: boolean;
  hasNotices?: boolean;
}

export interface UseVizPanelChildrenLayoutResponse {
  /**
   * Layout to apply to the header.
   */
  header: {
    show: boolean;
    height: number;
    showRelativeTimeChip: boolean;
  };
  timeOverrideBar: {
    show: boolean;
    height: number;
  };
}

/**
 * Hook that provides some constants related to the layout of the children within `VizContainer.Root` in `VizPanel`.
 *
 * See the other hooks in this file for related hooks for managing the layout of `VizPanel` children. These hooks are
 * separated because of various context dependencies.
 */
export const useVizPanelChildrenLayout = ({
  title,
  enableTimeOverrideBar,
  showRelativeTimeChip,
  hasErrors,
  hasNotices,
}: UseVizPanelChildrenLayoutProps): UseVizPanelChildrenLayoutResponse => {
  const containerLayout = useContainerLayout();
  const showHeader: boolean =
    !!title || !!hasErrors || !!hasNotices || (!!showRelativeTimeChip && !enableTimeOverrideBar);
  const headerHeight = showHeader ? containerLayout.header.height : 0;

  const showTimeOverrideBar = !!enableTimeOverrideBar && !!showRelativeTimeChip;
  const timeOverrideBarHeight = showTimeOverrideBar ? OVERRIDE_BAR_HEIGHT : 0;

  const header: UseVizPanelChildrenLayoutResponse['header'] = useMemo(() => {
    return {
      show: showHeader,
      height: headerHeight,
      // Only show the relative time chip if it's requested and we are not showing the time override bar
      showRelativeTimeChip: !!showRelativeTimeChip && !showTimeOverrideBar,
    };
  }, [headerHeight, showHeader, showRelativeTimeChip, showTimeOverrideBar]);

  const timeOverrideBar: UseVizPanelChildrenLayoutResponse['timeOverrideBar'] = useMemo(() => {
    return {
      show: showTimeOverrideBar,
      height: timeOverrideBarHeight,
    };
  }, [showTimeOverrideBar, timeOverrideBarHeight]);

  return useMemo(() => {
    return {
      header,
      timeOverrideBar,
    };
  }, [header, timeOverrideBar]);
};

export interface UseVizPanelContentLayoutProps extends Pick<VizPanelProps, 'width' | 'height'> {
  vizPanelChildrenLayout: UseVizPanelChildrenLayoutResponse;
}

export interface UseVizPanelContentLayoutResponse {
  width: number;
  height: number;

  /**
   * When true, the panel content is in a situation where it needs to responsively recalculate the
   * dimensions (e.g. opened in a modal).
   */
  needsResponsiveDimensions: boolean;
}

/**
 * Hook that provides some constants related to the layout of the `VizPanel` content.
 *
 * See the other hooks in this file for related hooks for managing the layout of `VizPanel` children. These hooks are
 * separated because of various context dependencies.
 */
export const useVizPanelContentLayout = ({
  width,
  height,
  vizPanelChildrenLayout,
}: UseVizPanelContentLayoutProps): UseVizPanelContentLayoutResponse => {
  const containerLayout = useContainerLayout();
  const vizContainerLayout = useVizContainerLayout();

  const { isNestedContainer } = useContainerContext();
  const { isParentContainerInModal, isInModal } = useIsContainerInModal();
  const { panwRebrandStubYear } = useFeatureFlags();

  return useMemo(() => {
    const showBorder = isNestedContainer || isParentContainerInModal || panwRebrandStubYear;
    const borderWidth = showBorder ? containerLayout.container.nestedBorderWidth : 0;

    const contentWidth =
      width -
      vizContainerLayout.container.paddingX * 2 -
      vizContainerLayout.containerBodyInner.paddingX * 2 -
      borderWidth * 2;
    const contentHeight =
      height -
      vizPanelChildrenLayout.header.height -
      vizPanelChildrenLayout.timeOverrideBar.height -
      vizContainerLayout.container.paddingY * 2 -
      vizContainerLayout.containerBodyInner.paddingY * 2 -
      borderWidth * 2;

    return {
      width: contentWidth,
      height: contentHeight,

      // We need to switch back to a responsive wrapper for the content when inside the modal because the
      // dimensions will need to change based on the modal's layout.
      needsResponsiveDimensions: isInModal,
    };
  }, [
    containerLayout.container.nestedBorderWidth,
    height,
    isInModal,
    isNestedContainer,
    isParentContainerInModal,
    vizContainerLayout.container.paddingX,
    vizContainerLayout.container.paddingY,
    vizContainerLayout.containerBodyInner.paddingX,
    vizContainerLayout.containerBodyInner.paddingY,
    vizPanelChildrenLayout.header.height,
    vizPanelChildrenLayout.timeOverrideBar.height,
    width,
    panwRebrandStubYear,
  ]);
};
